import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Crown, UserPlus, UserMinus, Trash2, RefreshCw, Mail,
  BarChart3, LifeBuoy, AlertTriangle, Clock, CheckCircle2, Ban,
} from "lucide-react";
import { DashboardShell, PageHead } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { journeyApi, apiError } from "@/lib/journeyApi";

const fmt = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString(); } catch { return iso; } };

const STATUS = {
  owner: { label: "Owner", icon: Crown, cls: "bg-[#FBEFD9] text-[#B45309]" },
  active: { label: "Active", icon: CheckCircle2, cls: "bg-[#DDE7DF] text-[#164C3B]" },
  pending: { label: "Invited · pending", icon: Clock, cls: "bg-[#FBF3E6] text-[#8A6D1F]" },
  revoked: { label: "Revoked", icon: Ban, cls: "bg-[#F1ECE6] text-[#8A7A68]" },
};

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.revoked;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${s.cls}`} data-testid={`status-${status}`}>
      <Icon className="h-3 w-3" /> {s.label}
    </span>
  );
}

export default function AdminAccess() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState("");
  const [del, setDel] = useState(null); // { email }
  const [confirmText, setConfirmText] = useState("");

  const load = () =>
    journeyApi.adminRolesList().then((d) => { setData(d); setLoading(false); })
      .catch((e) => { toast.error(apiError(e, "Could not load admin access.")); setLoading(false); });

  useEffect(() => { load(); }, []);

  const act = async (fn, label, successMsg) => {
    setBusy(label);
    try { const r = await fn(); toast.success(successMsg || `${label}: ${r.outcome || "done"}`); await load(); }
    catch (err) { toast.error(apiError(err, `${label} failed.`)); }
    finally { setBusy(""); }
  };

  const invite = async (e) => {
    e?.preventDefault?.();
    const q = email.trim().toLowerCase();
    if (!q.includes("@")) return toast.error("Enter a valid email address.");
    await act(() => journeyApi.adminRoleInvite(q), "Invite",
      "Admin invited. A secure setup link was emailed so they set their own password — no purchase needed.");
    setEmail("");
  };

  const confirmDelete = async () => {
    const target = del.email;
    if (confirmText.trim() !== `DELETE ${target}`) return toast.error(`Type 'DELETE ${target}' exactly to confirm.`);
    setBusy(`delete-${target}`);
    try {
      await journeyApi.adminRoleDelete(target, confirmText.trim());
      toast.success("Account deleted. Payment records are retained for accounting.");
      setDel(null); setConfirmText(""); await load();
    } catch (err) { toast.error(apiError(err, "Delete failed.")); }
    finally { setBusy(""); }
  };

  if (loading) return <DashboardShell><PageHead title="Admin access" backToHome homeTo="/dashboard/admin/analytics" /><div className="h-40 animate-pulse rounded-2xl bg-[#F0EBE2]" /></DashboardShell>;

  const members = data?.members || [];

  return (
    <DashboardShell>
      <PageHead title="Admin access" sub="Owner-only. Invite, resend, revoke or delete admin accounts. Invited admins set their own password via a secure link — you never see it, and no purchase is required." backToHome homeTo="/dashboard/admin/analytics" />

      <div className="mb-4 flex flex-wrap gap-2" data-testid="admin-access-nav">
        <Link to="/dashboard/admin/analytics" className="inline-flex items-center gap-2 rounded-xl pe-surface px-4 py-2 text-sm font-medium text-[#164C3B] hover:bg-[#F0EBE2]"><BarChart3 className="h-4 w-4" /> Admin Home</Link>
        <Link to="/dashboard/admin/support" className="inline-flex items-center gap-2 rounded-xl pe-surface px-4 py-2 text-sm font-medium text-[#164C3B] hover:bg-[#F0EBE2]"><LifeBuoy className="h-4 w-4" /> Support console</Link>
      </div>

      <form onSubmit={invite} className="flex flex-col gap-3 rounded-2xl pe-surface p-5 sm:flex-row sm:items-end" data-testid="admin-invite-form">
        <div className="flex-1">
          <label className="block text-sm font-medium text-[#70584A]">Invite an admin by email</label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@email.com" autoComplete="off" className="mt-1.5 rounded-xl border-[#E5E0D9]" data-testid="admin-invite-input" />
        </div>
        <Button type="submit" disabled={busy === "Invite"} className="rounded-xl bg-[#164C3B] text-white hover:bg-[#123b2e]" data-testid="admin-invite-button">
          <UserPlus className="mr-2 h-4 w-4" />{busy === "Invite" ? "Inviting…" : "Send invite"}
        </Button>
      </form>

      <div className="mt-6 rounded-2xl pe-surface p-6" data-testid="admin-members">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#A89A88]">Authorized access</p>
        <div className="mt-3 divide-y divide-[#F0EBE2]">
          {members.map((m) => {
            const dbusy = busy === `delete-${m.email}`;
            return (
              <div key={m.user_id || m.email} className="flex flex-wrap items-center justify-between gap-3 py-3.5" data-testid={`admin-member-${m.email}`}>
                <div className="flex min-w-0 items-center gap-3">
                  {m.status === "owner" ? <Crown className="h-4 w-4 shrink-0 text-[#B45309]" /> : <ShieldCheck className="h-4 w-4 shrink-0 text-[#164C3B]" />}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-[#171717]">{m.email}</p>
                    <div className="mt-1 flex items-center gap-2">
                      <StatusBadge status={m.status} />
                      {m.status !== "owner" && <span className="text-xs text-[#A89A88]">added {fmt(m.created_at)}</span>}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {m.status === "owner" && <span className="flex items-center gap-1 text-xs text-[#A89A88]"><Mail className="h-3.5 w-3.5" /> founder · protected</span>}

                  {m.status === "pending" && (
                    <Button onClick={() => act(() => journeyApi.adminRoleResend(m.email), "Resend", "Setup link re-sent (old link invalidated).")} disabled={!!busy} variant="outline" size="sm" className="rounded-xl border-[#E5E0D9] text-[#164C3B]" data-testid={`admin-resend-${m.email}`}>
                      <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Resend
                    </Button>
                  )}
                  {(m.status === "pending" || m.status === "active") && (
                    <Button onClick={() => act(() => journeyApi.adminRoleRevoke(m.email), "Revoke", m.status === "pending" ? "Invitation revoked." : "Admin access revoked. Their customer account (if any) is untouched.")} disabled={!!busy} variant="outline" size="sm" className="rounded-xl border-[#E5E0D9] text-[#8A6D1F]" data-testid={`admin-revoke-${m.email}`}>
                      <UserMinus className="mr-1.5 h-3.5 w-3.5" /> {m.status === "pending" ? "Revoke invite" : "Revoke access"}
                    </Button>
                  )}
                  {m.status === "revoked" && (
                    <Button onClick={() => act(() => journeyApi.adminRoleInvite(m.email), "Invite", "Re-invited. A fresh setup link was sent.")} disabled={!!busy} variant="outline" size="sm" className="rounded-xl border-[#E5E0D9] text-[#164C3B]" data-testid={`admin-reinvite-${m.email}`}>
                      <UserPlus className="mr-1.5 h-3.5 w-3.5" /> Re-invite
                    </Button>
                  )}
                  {m.status !== "owner" && (
                    <Button onClick={() => { setDel({ email: m.email }); setConfirmText(""); }} disabled={dbusy} variant="outline" size="sm" className="rounded-xl border-[#E7D4CE] text-[#B4231F] hover:bg-[#F7ECE8]" data-testid={`admin-delete-${m.email}`}>
                      <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-4 text-xs text-[#A89A88]">Admins can use the support console and analytics but cannot manage admin access. Only you (the owner) can invite, revoke or delete. The owner cannot be revoked or deleted, and there is exactly one owner. <strong>Revoke</strong> removes admin rights only; <strong>Delete</strong> permanently removes the account (payment records retained for accounting).</p>
      </div>

      {del && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="delete-modal" onClick={() => !busy && setDel(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-[#B4231F]"><AlertTriangle className="h-5 w-5" /><p className="font-serif text-xl text-[#171717]">Delete this account?</p></div>
            <p className="mt-3 text-sm leading-relaxed text-[#70584A]">This permanently deletes <strong>{del.email}</strong> and their app data. This is <strong>not</strong> the same as revoking admin access. Payment/entitlement records are retained only as required for accounting. This cannot be undone.</p>
            <label className="mt-4 block text-sm font-medium text-[#70584A]">Type <code className="rounded bg-[#F0EBE2] px-1.5 py-0.5 text-[#B4231F]">DELETE {del.email}</code> to confirm</label>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} autoFocus placeholder={`DELETE ${del.email}`} className="mt-1.5 rounded-xl border-[#E5E0D9]" data-testid="delete-confirm-input" />
            <div className="mt-5 flex justify-end gap-2">
              <Button onClick={() => setDel(null)} disabled={!!busy} variant="outline" className="rounded-xl border-[#E5E0D9]" data-testid="delete-cancel">Cancel</Button>
              <Button onClick={confirmDelete} disabled={!!busy || confirmText.trim() !== `DELETE ${del.email}`} className="rounded-xl bg-[#B4231F] text-white hover:bg-[#9a1d1a]" data-testid="delete-confirm-button">
                <Trash2 className="mr-1.5 h-4 w-4" /> {busy ? "Deleting…" : "Delete account"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
